import React, { useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import Gap from '../components/Gap'
import { firstUrlIn, sharedLinkKind } from '../utils/sharedLink'

/**
 * 다른 앱에서 'TASTEE로 공유'해 들어오는 **공용 입구**.
 *
 *   /share?url=<주소>&text=<공유 글 전체>
 *
 * 앱은 링크 종류를 가리지 않고 전부 여기로 보낸다. 무엇을 받을지 정하는 일은
 * 여기 한 곳에 모아 둔다 — 그래야 받는 범위를 넓힐 때 **앱을 다시 올리지
 * 않아도 된다**. 예전에는 앱이 인스타 주소만 골라내고 나머지를 버려서,
 * 네이버지도·카카오맵에서 공유하면 아무 일도 일어나지 않았다.
 *
 *   instagram  게시물·릴 → /share/instagram
 *   map        네이버지도·카카오맵 → /share/map
 *   그 밖       안내 화면
 *
 * 화면을 하나 거쳐 보내는 이유 — 주소만 보고 바로 갈라야 하는데, 그 판단을
 * 받는 쪽 화면마다 넣으면 규칙이 두 군데로 갈라진다. 판단은 sharedLink.ts
 * 한 곳에만 둔다 (앱 share_intent_handler.dart 와 같은 규칙).
 *
 * replace 로 넘기는 이유 — 이 화면은 지나가는 자리다. 히스토리에 남겨 두면
 * 담은 뒤 뒤로가기를 눌렀을 때 여기로 돌아와 다시 갈라지고, 사용자는 같은
 * 화면을 두 번 보게 된다.
 */
const SharedLink: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const text = params.get('text')

  /*
   * 주소를 정한다.
   *
   * 보통은 앱이 url 을 따로 실어 준다. 글만 올 때를 대비해 글에서도 한 번
   * 찾는다 — 앱을 거치지 않고 이 주소를 직접 여는 경우(테스트·브라우저)가
   * 있어서다.
   */
  const url = useMemo(() => {
    const given = params.get('url')?.trim()
    return given && given !== '' ? given : firstUrlIn(text)
  }, [params, text])

  const kind = useMemo(() => (url ? sharedLinkKind(url) : null), [url])

  useEffect(() => {
    if (!url || !kind) return

    const q = new URLSearchParams({ url })
    if (kind === 'instagram') {
      navigate(`/share/instagram?${q}`, { replace: true })
      return
    }

    /*
     * 지도 링크는 공유 글 전체도 같이 넘긴다.
     *
     * 카카오 가게 공유("[카카오맵] 타카사 https://kko.to/…")는 **가게 이름이
     * 유일한 단서**다. 단축 주소만으로는 서버가 어느 가게인지 알 수 없다.
     */
    if (text) q.set('text', text)
    navigate(`/share/map?${q}`, { replace: true })
  }, [url, kind, text, navigate])

  // 넘기는 중에는 아무것도 그리지 않는다. 한 칸 깜빡이고 사라질 화면이다.
  if (url && kind) return null

  const title = url ? '지원하지 않는 링크예요' : '링크를 찾지 못했어요'
  const body = url
    ? '네이버지도·카카오맵의 장소나 리스트, 또는 인스타그램 게시물을 공유해 주세요.'
    : '공유한 내용에 주소가 없었어요. 다시 공유해 주세요.'

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar title="가져오기" isBottomBorder />
      <div className="app-container flex-1 min-h-0 flex flex-col px-20px pt-20px overflow-hidden">
        <div className="mt-24px flex-1 flex flex-col items-center justify-center">
          <p className="text-style-button2 text-brown-80%-main">{title}</p>
          <Gap height={8} />
          <p className="text-14px leading-20px text-textColorText3 text-center">
            {body}
          </p>
        </div>

        <div className="pb-24px pt-8px">
          <button
            type="button"
            onClick={() => navigate('/search', { replace: true })}
            className="w-full py-16px rounded-full bg-4B3621-80% font-pretendard font-700 text-16px text-white"
          >
            직접 검색하기
          </button>
        </div>
      </div>
    </div>
  )
}

export default SharedLink
